import { fetchWithAuth } from './api';

const REST_BASE = (import.meta.env.VITE_REST_API_URL ?? '').replace(/\/$/, '');

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CurriculumArea {
  id: string;
  title: string;
  samplePrompts: string[];
  areaDescription?: string;
  storyExamples?: string[];
  order: number;
  status?: 'not_started' | 'in_progress' | 'complete';
  sessionCount?: number;
}

export interface CurriculumResponse {
  areas: CurriculumArea[];
}

export interface SuggestionResponse {
  area: CurriculumArea | null;
}

// ── API functions ──────────────────────────────────────────────────────────────

/**
 * Fetch the current user's curriculum.
 * Seed-on-first-read: first call returns the 15-area default list.
 */
export async function fetchCurriculum(): Promise<CurriculumResponse> {
  const response = await fetchWithAuth(`${REST_BASE}/curriculum`);
  if (!response.ok) {
    throw new Error(`GET /curriculum returned ${response.status}`);
  }
  return response.json() as Promise<CurriculumResponse>;
}

/**
 * Persist the full curriculum areas array.
 * Used for add, rename, remove, and reorder mutations.
 */
export async function saveCurriculum(areas: CurriculumArea[]): Promise<CurriculumResponse> {
  const response = await fetchWithAuth(`${REST_BASE}/curriculum`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ areas }),
  });
  if (!response.ok) {
    throw new Error(`PUT /curriculum returned ${response.status}`);
  }
  return response.json() as Promise<CurriculumResponse>;
}

/**
 * Fetch the suggested focus area for the next session.
 * Returns null area when no suggestion is available (all areas covered).
 */
export async function fetchSuggestion(): Promise<SuggestionResponse> {
  const response = await fetchWithAuth(`${REST_BASE}/suggestion`);
  if (!response.ok) {
    throw new Error(`GET /suggestion returned ${response.status}`);
  }
  return response.json() as Promise<SuggestionResponse>;
}

/**
 * Update the area tags on a completed session (TAG-02 tag review).
 * Tags is the full new array of area IDs for this session.
 */
export async function patchSessionTags(sessionId: string, tags: string[]): Promise<void> {
  const response = await fetchWithAuth(`${REST_BASE}/sessions/${sessionId}/tags`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ areaTags: tags }),
  });
  if (!response.ok) {
    throw new Error(`PATCH /sessions/${sessionId}/tags returned ${response.status}`);
  }
}
